import polyglotI18nProvider from 'ra-i18n-polyglot'
import type { I18nProvider, StringMap, TranslationMessages } from 'react-admin'

import type { BombeoCompletion, OperationKind, ProductClass, ScaleEventType } from '../data'
import type { ScaleState } from '../data/source/summarise'
import type { MarpolAnnex } from '../data/source/types'
import catalanMessages from './ra-language-catalan'
import spanishMessages from './ra-language-spanish'

export const availableLocales = [
  { locale: 'es', name: 'Castellano' },
  { locale: 'ca', name: 'Català' },
] as const
export const LOCALES = availableLocales.map(({ locale }) => locale)
type Locale = (typeof LOCALES)[number]

const isLocaleSupported = (locale: string): locale is Locale => LOCALES.some(supported => supported === locale)

export const getTranslations = (locale: string): Translations => {
  if (!isLocaleSupported(locale)) {
    throw new Error(`Unsupported locale: ${locale}`)
  }
  switch (locale) {
    case 'es':
      return es
    case 'ca':
      return ca
  }
}

interface ReadLabels extends StringMap {
  title: string
  action: string
  in_progress: string
  busy: string
  refused: string
  never: string
}

export interface Translations extends TranslationMessages {
  common: {
    back: string
  }
  pages: {
    hidden: {
      title: string
      sostrat: ReadLabels
      ais: ReadLabels & {
        counts: { opened: string; closed: string; written: string }
      }
      run: {
        last_run: string
        requested_at: string
        finished_at: string
        counts: string
        problems: string
        status: { REQUESTED: string; RUNNING: string; SUCCEEDED: string; FAILED: string }
        trigger: { MANUAL: string; SCHEDULE: string }
      }
    }
    scales: {
      add_special: string
      no_events: string
      timeline: {
        marker: string
        sostrat: string
        sostrat_hint: string
        end_unconfirmed: string
        ais: {
          arrival: string
          departure: string
          difference: string
          duration: string
          no_difference: string
          first_seen: string
          level: { GREEN: string; AMBER: string; RED: string }
          other_muelle: string
        }
        stay_times: {
          arrival: string
          departure: string
          ais: string
          not_compared: string
          check: string
          status: { AGREES: string; TIMES_DIFFER: string; MUELLE_DIFFERS: string; NOT_COMPARED: string }
          basis: { actual: string; planned: string; unconfirmed: string }
        }
      }
      stay_dates: {
        title: string
        muelle: string
        arrival: string
        departure: string
        planned: string
        actual: string
        difference: string
        phase: string
        not_recorded: string
        signed: string
        ago: string
        phases: { OVERDUE: string; UNCONFIRMED: string; DEPARTED: string; BERTHED: string; PLANNED: string }
      }
      warnings: {
        title: string
        short: string
        fast: string
        implausible_quantity: string
        outside_stay: string
        missing_product: string
        missing_tonnes: string
        minutes: string
        hours: string
        whole_hours: string
      }
      stay: {
        state: string
        fondeo_previo: string
        anterior: string
        alternativos: string
        sostrat_terminal: string
        yes: string
        no: string
        tonnes: string
        read_as_kg: string
        implausible: string
        window: string
      }
      metrics: {
        title: string
        inicio: string
        rendimiento: string
        salida: string
        hours: string
        hours_per_day: string
      }
      info: {
        button: string
        title: string
      }
    }
  }
  components: {
    manual_scale: {
      label: string
      label_seen_in_source: string
    }
  }
  resources: {
    scales: {
      name: string
      fields: {
        scaleId: string
        year: string
        state: string
        finished: string
        currentMuelle: string
        currentStartTime: string
        eventMarkers: string
        bombeoCompletion: string
        vessel: string
        imo: string
        eslora: string
        bcm: string
        consignataria: string
        eta: string
        etd: string
        firstEta: string
        firstEtd: string
        terminals: string
      }
      filters: {
        q: string
        active: string
        finished: string
        all: string
      }
      actions: {
        back_to_list: string
      }
    }
    scale_events: {
      name: string
      title_create: string
      title_edit: string
      actions: {
        back_to_scale: string
      }
      fields: {
        scaleId: string
        type: string
        startTime: string
        endTime: string
        connectionPoint: string
        productClass: string
        product: string
        quantityT: string
        operationKind: string
        marpolAnnex: string
        marpolNoticeAt: string
      }
      helpers: {
        endTime: string
        prefilled: string
      }
      errors: {
        not_special: string
        not_found: string
        invalid_start: string
        invalid_end: string
        end_before_start: string
        scale_closed: string
        no_stays: string
        start_outside_stays: string
      }
    }
  }
  enums: {
    ScaleEventType: Record<ScaleEventType, string>
    ProductClass: Record<ProductClass, string>
    OperationKind: Record<OperationKind, string>
    MarpolAnnex: Record<MarpolAnnex, string>
    ScaleState: Record<ScaleState, string>
    BombeoCompletion: Record<BombeoCompletion, string>
  }
}

export type TranslationKey = RecursiveKeyOf<Translations>

type RecursiveKeyOf<TObj extends object> = {
  [TKey in keyof TObj & (string | number)]: TObj[TKey] extends object ? `${TKey}` | `${TKey}.${RecursiveKeyOf<TObj[TKey]>}` : `${TKey}`
}[keyof TObj & (string | number)]

export const translate = (key: string, translations: Translations): string => {
  const segments = key.split('.')
  let current: unknown = translations
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      return key
    }
    current = Reflect.get(current, segment)
    if (current === undefined) {
      return key
    }
  }
  if (typeof current === 'string') {
    return current
  }
  return key
}

export const es: Translations = {
  ...spanishMessages,
  common: {
    back: 'Volver',
  },
  pages: {
    hidden: {
      title: 'Datos de SÒSTRAT y AIS',
      sostrat: {
        title: 'SÒSTRAT',
        action: 'Actualizar SÒSTRAT',
        in_progress: 'Leyendo SÒSTRAT: %{status}',
        busy: 'Ya hay una lectura de SÒSTRAT en curso, desde %{since}.',
        refused: 'GitHub no ha iniciado la lectura: %{message}',
        never: 'SÒSTRAT no se ha leído todavía.',
      },
      ais: {
        title: 'AIS',
        action: 'Actualizar AIS',
        in_progress: 'Leyendo AIS: %{status}',
        busy: 'Ya hay una lectura de AIS en curso, desde %{since}.',
        refused: 'No se ha iniciado la lectura de AIS: %{message}',
        never: 'AIS no se ha leído todavía.',
        counts: { opened: 'estancias abiertas', closed: 'estancias cerradas', written: 'comprobaciones escritas' },
      },
      run: {
        last_run: 'Última lectura (%{trigger})',
        requested_at: 'Solicitada',
        finished_at: 'Terminada',
        counts: 'Escrito',
        problems: 'Problemas',
        status: { REQUESTED: 'Solicitada', RUNNING: 'En curso', SUCCEEDED: 'Correcta', FAILED: 'Fallida' },
        trigger: { MANUAL: 'manual', SCHEDULE: 'programada' },
      },
    },
    scales: {
      add_special: 'Añadir %{type}',
      no_events: 'Sin eventos',
      timeline: {
        marker: '%{event} #%{number}',
        sostrat: 'SÒSTRAT',
        sostrat_hint: 'Del SÒSTRAT (ventana declarada); no editable',
        end_unconfirmed: 'fin previsto, sin confirmar',
        ais: {
          arrival: 'Llegada AIS',
          departure: 'Salida AIS',
          difference: 'AIS − SÒSTRAT: %{difference}',
          duration: '%{sign}%{hours} h %{minutes} min',
          no_difference: 'no comparada con el SÒSTRAT',
          first_seen: 'AIS lo oyó ya atracado: es cuando lo vio por primera vez, no cuando llegó',
          level: {
            GREEN: 'coincide (hasta %{max} min)',
            AMBER: 'desfase moderado (hasta %{max} min)',
            RED: 'desfase grande (más de %{min} min)',
          },
          other_muelle: 'AIS lo vio en %{muelle}',
        },
        stay_times: {
          arrival: 'Atraque',
          departure: 'Desatraque',
          ais: 'AIS',
          not_compared: 'no comparada',
          check: 'Comprobación AIS: %{status}',
          status: {
            AGREES: 'coincide con el SÒSTRAT',
            TIMES_DIFFER: 'horas distintas del SÒSTRAT',
            MUELLE_DIFFERS: 'otro muelle que el SÒSTRAT',
            NOT_COMPARED: 'sin comparar todavía',
          },
          basis: { actual: 'real', planned: 'previsto', unconfirmed: 'previsto, sin confirmar' },
        },
      },
      stay_dates: {
        title: 'Estancias: fechas previstas y reales',
        muelle: 'Muelle',
        arrival: 'Atraque',
        departure: 'Desatraque',
        planned: 'Previsto',
        actual: 'Real',
        difference: 'Real − previsto',
        phase: 'Situación',
        not_recorded: 'sin registrar',
        signed: '%{sign}%{hours} h %{minutes} min',
        ago: '%{hours} h %{minutes} min',
        phases: {
          OVERDUE: 'Desatraque previsto hace %{ago}, sin desatraque registrado',
          UNCONFIRMED: 'Fin previsto, sin confirmar',
          DEPARTED: 'Desatracado',
          BERTHED: 'Atracado',
          PLANNED: 'Previsto',
        },
      },
      warnings: {
        title: 'Avisos',
        short: 'dura solo %{duration}',
        fast: '%{rate} t/h: %{duration} es demasiado poco para sus toneladas',
        implausible_quantity: 'cantidad dudosa de %{goods} en el SÒSTRAT: %{quantity} %{unit}',
        outside_stay: 'el SÒSTRAT la sitúa del %{start} al %{end}, fuera de su estancia (%{stay_start} – %{stay_end})',
        missing_product: 'sin producto',
        missing_tonnes: 'sin toneladas',
        minutes: '%{minutes} min',
        hours: '%{hours} h %{minutes} min',
        whole_hours: '%{hours} h',
      },
      stay: {
        state: 'Estado',
        fondeo_previo: 'Fondeo previo',
        anterior: 'Anterior',
        alternativos: 'Muelles alternativos',
        sostrat_terminal: 'Terminal (SOSTRAT)',
        yes: 'Sí',
        no: 'No',
        tonnes: '%{quantity} t',
        read_as_kg: 'declarada en kg',
        implausible: 'cantidad dudosa: %{quantity} %{unit}',
        window: 'Ventana declarada',
      },
      metrics: {
        title: 'Métricas',
        inicio: 'Inicio',
        rendimiento: 'Rendimiento',
        salida: 'Salida',
        hours: '%{value} h',
        hours_per_day: '%{value} h/24h',
      },
      info: {
        button: '+Info',
        title: 'Escala %{scaleId} (%{year})',
      },
    },
  },
  components: {
    manual_scale: {
      label: 'Escala creada manualmente',
      label_seen_in_source: 'Escala creada manualmente y confirmada por el origen',
    },
  },
  resources: {
    scales: {
      name: 'Escala |||| Escalas',
      fields: {
        scaleId: 'Escala',
        year: 'Año',
        state: 'Estado',
        finished: 'Finalizada',
        currentMuelle: 'Muelle',
        currentStartTime: 'Inicio',
        eventMarkers: 'Eventos',
        bombeoCompletion: 'Completo',
        vessel: 'Buque',
        imo: 'IMO',
        eslora: 'Eslora',
        bcm: 'BCM',
        consignataria: 'Consignataria',
        eta: 'ETA',
        etd: 'ETD',
        firstEta: 'Primera ETA',
        firstEtd: 'Primera ETD',
        terminals: 'Empresa',
      },
      filters: {
        q: 'Buscar',
        active: 'Activas',
        finished: 'Finalizadas',
        all: 'Todas',
      },
      actions: {
        back_to_list: 'Volver a la lista',
      },
    },
    scale_events: {
      name: 'Evento |||| Eventos',
      title_create: 'Nuevo evento',
      title_edit: 'Editar evento',
      actions: {
        back_to_scale: 'Volver a la escala',
      },
      fields: {
        scaleId: 'Escala',
        type: 'Tipo',
        startTime: 'Inicio',
        endTime: 'Fin',
        connectionPoint: 'Código de conexión',
        productClass: 'Producto',
        product: 'Producto exacto',
        quantityT: 'Cantidad (t)',
        operationKind: 'Tipo de operación',
        marpolAnnex: 'Tipo MARPOL',
        marpolNoticeAt: 'Hora aviso servicio MARPOL',
      },
      helpers: {
        endTime: 'Vacío = en curso',
        prefilled: 'Precargado desde SOSTRAT: confírmalo con el CIDO',
      },
      errors: {
        not_special: 'Este evento no se puede editar',
        not_found: 'No se ha encontrado el evento',
        invalid_start: 'Inicio no válido',
        invalid_end: 'Fin no válido',
        end_before_start: 'El fin no puede ser anterior al inicio',
        scale_closed: 'La escala ha terminado: ya no se le pueden añadir eventos',
        no_stays: 'La escala aún no tiene ningún atraque: no se le pueden añadir eventos MARPOL ni BOMBEO',
        start_outside_stays: 'El inicio tiene que caer dentro de un atraque de la escala',
      },
    },
  },
  enums: {
    ScaleEventType: {
      PLACE: 'Ubicación',
      MARPOL: 'MARPOL',
      BOMBEO: 'BOMBEO',
    },
    ProductClass: {
      HYDROCARBON: 'Hidrocarburo',
      CHEMICAL: 'Producto químico',
    },
    OperationKind: {
      LOAD: 'Carga',
      UNLOAD: 'Descarga',
    },
    MarpolAnnex: {
      I: 'MARPOL I',
      V: 'MARPOL V',
    },
    ScaleState: {
      PLANNED: 'Planificado',
      IN_PROGRESS: 'En curso',
      CLOSED: 'Cerrado',
      FINISHED: 'Finalizado',
    },
    BombeoCompletion: {
      COMPLETE: 'Completo',
      INCOMPLETE: 'Incompleto',
      UNKNOWN: '—',
    },
  },
}

export const ca: Translations = {
  ...catalanMessages,
  common: {
    back: 'Tornar',
  },
  pages: {
    hidden: {
      title: "Dades de SÒSTRAT i d'AIS",
      sostrat: {
        title: 'SÒSTRAT',
        action: 'Actualitzar SÒSTRAT',
        in_progress: 'Llegint SÒSTRAT: %{status}',
        busy: 'Ja hi ha una lectura de SÒSTRAT en curs, des de %{since}.',
        refused: 'GitHub no ha iniciat la lectura: %{message}',
        never: "SÒSTRAT encara no s'ha llegit.",
      },
      ais: {
        title: 'AIS',
        action: 'Actualitzar AIS',
        in_progress: 'Llegint AIS: %{status}',
        busy: "Ja hi ha una lectura d'AIS en curs, des de %{since}.",
        refused: "No s'ha iniciat la lectura d'AIS: %{message}",
        never: "AIS encara no s'ha llegit.",
        counts: { opened: 'estades obertes', closed: 'estades tancades', written: 'comprovacions escrites' },
      },
      run: {
        last_run: 'Darrera lectura (%{trigger})',
        requested_at: 'Sol·licitada',
        finished_at: 'Acabada',
        counts: 'Escrit',
        problems: 'Problemes',
        status: { REQUESTED: 'Sol·licitada', RUNNING: 'En curs', SUCCEEDED: 'Correcta', FAILED: 'Fallida' },
        trigger: { MANUAL: 'manual', SCHEDULE: 'programada' },
      },
    },
    scales: {
      add_special: 'Afegir %{type}',
      no_events: 'Sense esdeveniments',
      timeline: {
        marker: '%{event} #%{number}',
        sostrat: 'SÒSTRAT',
        sostrat_hint: 'Del SÒSTRAT (finestra declarada); no editable',
        end_unconfirmed: 'final previst, sense confirmar',
        ais: {
          arrival: 'Arribada AIS',
          departure: 'Sortida AIS',
          difference: 'AIS − SÒSTRAT: %{difference}',
          duration: '%{sign}%{hours} h %{minutes} min',
          no_difference: 'no comparada amb el SÒSTRAT',
          first_seen: 'AIS el va sentir ja atracat: és quan el va veure per primer cop, no quan va arribar',
          level: {
            GREEN: 'coincideix (fins a %{max} min)',
            AMBER: 'desfasament moderat (fins a %{max} min)',
            RED: 'desfasament gran (més de %{min} min)',
          },
          other_muelle: "AIS l'ha vist al moll %{muelle}",
        },
        stay_times: {
          arrival: 'Atracada',
          departure: 'Desatracada',
          ais: 'AIS',
          not_compared: 'no comparada',
          check: 'Comprovació AIS: %{status}',
          status: {
            AGREES: 'coincideix amb el SÒSTRAT',
            TIMES_DIFFER: 'hores diferents del SÒSTRAT',
            MUELLE_DIFFERS: 'altre moll que el SÒSTRAT',
            NOT_COMPARED: 'sense comparar encara',
          },
          basis: { actual: 'real', planned: 'prevista', unconfirmed: 'prevista, sense confirmar' },
        },
      },
      stay_dates: {
        title: 'Estades: dates previstes i reals',
        muelle: 'Moll',
        arrival: 'Atracada',
        departure: 'Desatracada',
        planned: 'Prevista',
        actual: 'Real',
        difference: 'Real − prevista',
        phase: 'Situació',
        not_recorded: 'sense registrar',
        signed: '%{sign}%{hours} h %{minutes} min',
        ago: '%{hours} h %{minutes} min',
        phases: {
          OVERDUE: 'Desatracada prevista fa %{ago}, sense desatracada registrada',
          UNCONFIRMED: 'Final previst, sense confirmar',
          DEPARTED: 'Desatracat',
          BERTHED: 'Atracat',
          PLANNED: 'Prevista',
        },
      },
      warnings: {
        title: 'Avisos',
        short: 'dura només %{duration}',
        fast: '%{rate} t/h: %{duration} és massa poc per a les seves tones',
        implausible_quantity: 'quantitat dubtosa de %{goods} al SÒSTRAT: %{quantity} %{unit}',
        outside_stay: 'el SÒSTRAT la situa del %{start} al %{end}, fora de la seva estada (%{stay_start} – %{stay_end})',
        missing_product: 'sense producte',
        missing_tonnes: 'sense tones',
        minutes: '%{minutes} min',
        hours: '%{hours} h %{minutes} min',
        whole_hours: '%{hours} h',
      },
      stay: {
        state: 'Estat',
        fondeo_previo: 'Fondeig previ',
        anterior: 'Anterior',
        alternativos: 'Molls alternatius',
        sostrat_terminal: 'Terminal (SOSTRAT)',
        yes: 'Sí',
        no: 'No',
        tonnes: '%{quantity} t',
        read_as_kg: 'declarada en kg',
        implausible: 'quantitat dubtosa: %{quantity} %{unit}',
        window: 'Finestra declarada',
      },
      metrics: {
        title: 'Mètriques',
        inicio: 'Inici',
        rendimiento: 'Rendiment',
        salida: 'Sortida',
        hours: '%{value} h',
        hours_per_day: '%{value} h/24h',
      },
      info: {
        button: '+Info',
        title: 'Escala %{scaleId} (%{year})',
      },
    },
  },
  components: {
    manual_scale: {
      label: 'Escala creada manualment',
      label_seen_in_source: "Escala creada manualment i confirmada per l'origen",
    },
  },
  resources: {
    scales: {
      name: 'Escala |||| Escales',
      fields: {
        scaleId: 'Escala',
        year: 'Any',
        state: 'Estat',
        finished: 'Finalitzada',
        currentMuelle: 'Moll',
        currentStartTime: 'Inici',
        eventMarkers: 'Esdeveniments',
        bombeoCompletion: 'Complet',
        vessel: 'Vaixell',
        imo: 'IMO',
        eslora: 'Eslora',
        bcm: 'BCM',
        consignataria: 'Consignatària',
        eta: 'ETA',
        etd: 'ETD',
        firstEta: 'Primera ETA',
        firstEtd: 'Primera ETD',
        terminals: 'Empresa',
      },
      filters: {
        q: 'Cercar',
        active: 'Actives',
        finished: 'Finalitzades',
        all: 'Totes',
      },
      actions: {
        back_to_list: 'Tornar a la llista',
      },
    },
    scale_events: {
      name: 'Esdeveniment |||| Esdeveniments',
      title_create: 'Nou esdeveniment',
      title_edit: 'Editar esdeveniment',
      actions: {
        back_to_scale: "Tornar a l'escala",
      },
      fields: {
        scaleId: 'Escala',
        type: 'Tipus',
        startTime: 'Inici',
        endTime: 'Fi',
        connectionPoint: 'Codi de connexió',
        productClass: 'Producte',
        product: 'Producte exacte',
        quantityT: 'Quantitat (t)',
        operationKind: "Tipus d'operació",
        marpolAnnex: 'Tipus MARPOL',
        marpolNoticeAt: 'Hora avís servei MARPOL',
      },
      helpers: {
        endTime: 'Buit = en curs',
        prefilled: 'Precarregat des de SOSTRAT: confirma-ho amb el CIDO',
      },
      errors: {
        not_special: 'Aquest esdeveniment no es pot editar',
        not_found: "No s'ha trobat l'esdeveniment",
        invalid_start: 'Inici no vàlid',
        invalid_end: 'Fi no vàlida',
        end_before_start: "La fi no pot ser anterior a l'inici",
        scale_closed: "L'escala ha acabat: ja no s'hi poden afegir esdeveniments",
        no_stays: "L'escala encara no té cap atracament: no s'hi poden afegir esdeveniments MARPOL ni BOMBEO",
        start_outside_stays: "L'inici ha de caure dins d'un atracament de l'escala",
      },
    },
  },
  enums: {
    ScaleEventType: {
      PLACE: 'Ubicació',
      MARPOL: 'MARPOL',
      BOMBEO: 'BOMBEO',
    },
    ProductClass: {
      HYDROCARBON: 'Hidrocarbur',
      CHEMICAL: 'Producte químic',
    },
    OperationKind: {
      LOAD: 'Càrrega',
      UNLOAD: 'Descàrrega',
    },
    MarpolAnnex: {
      I: 'MARPOL I',
      V: 'MARPOL V',
    },
    ScaleState: {
      PLANNED: 'Planificat',
      IN_PROGRESS: 'En curs',
      CLOSED: 'Tancat',
      FINISHED: 'Finalitzat',
    },
    BombeoCompletion: {
      COMPLETE: 'Complet',
      INCOMPLETE: 'Incomplet',
      UNKNOWN: '—',
    },
  },
}

export const i18nProvider: I18nProvider = polyglotI18nProvider((locale: string) => getTranslations(locale), 'es', availableLocales)
